import React from 'react'
import { Link } from 'react-router-dom'

import type { InsuranceType } from 'types'

import FlexContent from 'components/base/FlexContent'
import Content, { Theme as ContentTheme } from 'components/base/Content'
import Title from 'components/base/Title'
import Paragraph, { Theme as ParagraphTheme } from 'components/base/Paragraph'
import Form from 'components/base/Form'
import InsuranceTypeRadio from 'components/base/Form/InsuranceTypeRadio'
import InsuranceTypeRadioComingSoon from 'components/base/Form/InsuranceTypeRadio/ComingSoon'
import Button from 'components/base/Button'
import UserSuppliedText from 'components/base/UserSuppliedText'

import QuotingWrapper, { Theme as QuotingWrapperTheme } from 'components/blocks/QuotingWrapper'

import SuggestInsuranceForm from 'components/blocks/SuggestInsuranceForm'

import {
  events,
  useGoogleTagManager,
} from 'hooks/useGoogleTagManager'

import { Props } from './types'

import {
  CityName,
  ConciergeIconLoading,
} from './styles'

const QuotingInsuranceTypes: React.FC<Props> = ({
  applicantCity,
  applicantFirstName,
  applicantStateAbbr,
  availableInsuranceTypes,
  isInsuranceTypesCached,
  isInsuranceTypesLoading,
  nextUrl,
  onChangeInsuranceType,
  selectedInsuranceTypes,
}): React.ReactElement => {
  const { pushDataLayerEvent } = useGoogleTagManager()

  const applicantLocation =
    (applicantCity && applicantStateAbbr && (
      <span>
        <CityName>{applicantCity.toLowerCase()}</CityName>,{' '}
        {applicantStateAbbr}
      </span>
    )) ||
    applicantStateAbbr ||
    'your area'

  const conciergeText = (isInsuranceTypesCached && availableInsuranceTypes && availableInsuranceTypes.length && 'Voilà!') || 'Dang!'

  return (
    <QuotingWrapper noPadding={true} theme={QuotingWrapperTheme.concierge} title={(isInsuranceTypesLoading && <ConciergeIconLoading />) || conciergeText}>
      <Content center={true} theme={ContentTheme.paddedBelow}>
        {(!isInsuranceTypesCached && !isInsuranceTypesLoading && (
          <Paragraph theme={ParagraphTheme.centered}>
            An error has occurred, unable to load insurance types.
          </Paragraph>
        )) || (availableInsuranceTypes && availableInsuranceTypes.length && (
          <>
            <Title level={3}>
              Here’s what we have to offer in {applicantLocation}.
              {(availableInsuranceTypes &&
                ' Which insurance would you like me to quote?') ||
                undefined}
            </Title>
            <Paragraph theme={ParagraphTheme.intro} strong={true} short={true}>
              Select one.
            </Paragraph>
            <Form>
              <FlexContent bottomBorder={true}>
                {availableInsuranceTypes.map(({
                  isAvailableOnline,
                  name,
                  slug,
                }) => (isAvailableOnline && (
                  <InsuranceTypeRadio
                    key={slug}
                    value={slug}
                    name='insurance-type'
                    insuranceType={slug as InsuranceType}
                    isChecked={selectedInsuranceTypes[slug]}
                    onChange={(isChecked: boolean): void => onChangeInsuranceType(slug, isChecked)}
                  >
                    {name}
                  </InsuranceTypeRadio>
                )) || (
                  <InsuranceTypeRadioComingSoon
                    key={slug}
                    insuranceType={slug as InsuranceType}
                    name='insurance-type'
                  >
                    {name}
                  </InsuranceTypeRadioComingSoon>
                ))}
              </FlexContent>
              <Content center={true} theme={ContentTheme.paddedBelow}>
                <Link
                  to={nextUrl || '?'}
                  onClick={() => pushDataLayerEvent({
                    ...events.select_item,
                    product_type: Object.keys(selectedInsuranceTypes).find(key => selectedInsuranceTypes[key as InsuranceType]) as InsuranceType,
                  })}
                >
                  <Button center={true} wide={true} disabled={!nextUrl}>
                    Next
                  </Button>
                </Link>
              </Content>
            </Form>
          </>
        )) || (
          <>
            <Title level={3}>
              Thanks
              <UserSuppliedText>{(applicantFirstName && ` ${applicantFirstName}`) || undefined}</UserSuppliedText>!
              We don’t currently offer insurance products in{' '}
              {applicantLocation}.
            </Title>
            <Paragraph short={true}>
              Please enter your email address, and the insurance products
              you’re looking for, and we’ll notify you when we launch in
              your area.
            </Paragraph>

            <SuggestInsuranceForm />
          </>
        )}
      </Content>
    </QuotingWrapper>
  )
}

export default QuotingInsuranceTypes
